import { Nullable, RecursiveStatic } from '@diamond/shared/schema';
import { Type } from '@sinclair/typebox';

// OcrDisplayError code literals — must stay in sync with OcrDisplayErrorCode in payment-proof-ocr.ts
export const OcrDisplayErrorCodeSchema = Type.Union([
  Type.Literal('DUPLICATE_PAYMENT_PROOF'),
  Type.Literal('OCR_UNREADABLE'),
  Type.Literal('OCR_UNAVAILABLE'),
  Type.Literal('OCR_TIMEOUT'),
  Type.Literal('PAYMENT_PROOF_AMOUNT_MISMATCH'),
  Type.Literal('RECIPIENT_MISMATCH'),
  Type.Literal('INVALID_FILE_TYPE'),
  Type.Literal('FILE_TOO_LARGE'),
  Type.Literal('MAX_FILES_EXCEEDED'),
  Type.Literal('OCR_JOB_NOT_READY'),
]);

export const OcrDisplayErrorSchema = Type.Object({
  code: OcrDisplayErrorCodeSchema,
  message: Type.String(),
});

export const OcrResultSchema = Type.Object({
  sender_bank: Nullable(Type.String()),
  sender_account_name: Type.Optional(Nullable(Type.String())),
  recipient_name: Nullable(Type.String()),
  recipient_account_number: Nullable(Type.String()),
  amount: Nullable(Type.Integer()),
  transaction_note: Nullable(Type.String()),
  reference_number: Nullable(Type.String()),
  transaction_datetime: Nullable(Type.String({ format: 'date-time' })),
  confidence_score: Nullable(Type.Number()),
});

export const OcrManualOverrideSchema = Type.Object({
  sender_bank: Nullable(Type.String()),
  sender_account_name: Nullable(Type.String()),
  recipient_name: Nullable(Type.String()),
  amount: Nullable(Type.Integer()),
  transaction_datetime: Nullable(Type.String({ format: 'date-time' })),
  manual_input_reason: Nullable(Type.String()),
  manual_input_at: Nullable(Type.String({ format: 'date-time' })),
});

const OcrEffectiveResultSourceSchema = Type.Union([
  Type.Literal('ocr'),
  Type.Literal('manual'),
]);
const NullableOcrEffectiveResultSourceSchema = Type.Union([
  OcrEffectiveResultSourceSchema,
  Type.Null(),
]);

export const OcrEffectiveResultSchema = Type.Object({
  sender_bank: Nullable(Type.String()),
  sender_account_name: Nullable(Type.String()),
  recipient_name: Nullable(Type.String()),
  recipient_account_number: Nullable(Type.String()),
  amount: Nullable(Type.Integer()),
  transaction_note: Nullable(Type.String()),
  reference_number: Nullable(Type.String()),
  transaction_datetime: Nullable(Type.String({ format: 'date-time' })),
  confidence_score: Nullable(Type.Number()),
  sender_bank_source: NullableOcrEffectiveResultSourceSchema,
  recipient_name_source: NullableOcrEffectiveResultSourceSchema,
  amount_source: NullableOcrEffectiveResultSourceSchema,
  transaction_datetime_source: NullableOcrEffectiveResultSourceSchema,
});

export const OcrItemSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  file_name: Type.String(),
  preview_url: Type.String(),
  ocr_result: Nullable(OcrResultSchema),
  manual_override: Nullable(OcrManualOverrideSchema),
  effective_result: Nullable(OcrEffectiveResultSchema),
  display_error: Nullable(OcrDisplayErrorSchema),
});

export const FinalizedPaymentProofOcrOriginalSchema = Type.Object({
  sender_bank: Nullable(Type.String()),
  recipient_name: Nullable(Type.String()),
  recipient_account_number: Nullable(Type.String()),
  amount: Nullable(Type.Integer()),
  transaction_datetime: Nullable(Type.String({ format: 'date-time' })),
});

export const FinalizedPaymentProofOcrManualOverrideSchema = Type.Object({
  sender_bank: Nullable(Type.String()),
  recipient_name: Nullable(Type.String()),
  amount: Nullable(Type.Integer()),
  transaction_datetime: Nullable(Type.String({ format: 'date-time' })),
  manual_input_reason: Nullable(Type.String()),
  manual_input_at: Nullable(Type.String({ format: 'date-time' })),
});

export const FinalizedPaymentProofOcrSnapshotSchema = Type.Object({
  sender_bank: Nullable(Type.String()),
  sender_account_name: Nullable(Type.String()),
  recipient_name: Nullable(Type.String()),
  recipient_account_number: Nullable(Type.String()),
  amount: Nullable(Type.Integer()),
  amount_source: NullableOcrEffectiveResultSourceSchema,
  recipient_name_source: NullableOcrEffectiveResultSourceSchema,
  transaction_datetime_source: NullableOcrEffectiveResultSourceSchema,
  sender_bank_source: NullableOcrEffectiveResultSourceSchema,
  transaction_datetime: Nullable(Type.String({ format: 'date-time' })),
  reference_number: Nullable(Type.String()),
  confidence_score: Nullable(Type.Number()),
  original_ocr: Nullable(FinalizedPaymentProofOcrOriginalSchema),
  manual_override: Nullable(FinalizedPaymentProofOcrManualOverrideSchema),
});

export const FinalizedPaymentProofItemSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  url: Type.String(),
  original_file_name: Nullable(Type.String()),
  created_at: Type.String({ format: 'date-time' }),
  ocr: Nullable(FinalizedPaymentProofOcrSnapshotSchema),
});

export const ManualOverrideSchema = Type.Object({
  ocr_job_id: Type.String({ format: 'uuid' }),
  amount: Type.Optional(Type.Integer({ minimum: 0 })),
  recipient_name: Type.Optional(Type.String()),
  transaction_datetime: Type.Optional(Type.String()),
  sender_bank: Type.Optional(Type.String()),
  sender_account_name: Type.String(),
});

export const PaymentProofOcrCrudSchema = {
  path: '/payment-proof/ocr',

  // POST /payment-proof/ocr
  uploadOcr: {
    path: '',
    body: Type.Object({
      order_payment_id: Type.String({ format: 'uuid' }),
    }),
    response: Type.Object({
      items: Type.Array(OcrItemSchema),
    }),
  },

  // GET /payment-proof/ocr
  getOcr: {
    path: '',
    query: Type.Object({
      order_payment_id: Type.String({ format: 'uuid' }),
    }),
    response: Type.Object({
      items: Type.Array(OcrItemSchema),
    }),
  },

  // DELETE /payment-proof/ocr/:id
  deleteOcr: {
    path: '/:id',
    params: Type.Object({
      id: Type.String({ format: 'uuid' }),
    }),
    response: Type.Object({
      message: Type.String(),
    }),
  },

};

export type PaymentProofOcrCrud = RecursiveStatic<
  typeof PaymentProofOcrCrudSchema
>;

export const OrderPaymentProofCrudSchema = {
  path: '/order-payments',

  // POST /order-payments/:order_payment_id/payment-proofs
  submitPaymentProofs: {
    path: '/:order_payment_id/payment-proofs',
    params: Type.Object({
      order_payment_id: Type.String({ format: 'uuid' }),
    }),
    body: Type.Object({
      ocr_job_ids: Type.Array(Type.String({ format: 'uuid' })),
      manual_overrides: Type.Array(ManualOverrideSchema),
    }),
    response: Type.Object({
      message: Type.String(),
    }),
  },
};

export type OrderPaymentProofCrud = RecursiveStatic<
  typeof OrderPaymentProofCrudSchema
>;
